/**
 * The editor of the exposed parameters, driven by the params schema of the image version.
 *
 * The schema is the one the clawlet validates a revision with (plane/README.md, "Contratto
 * condiviso"): the form offers exactly the fields it declares, and validates them here first so
 * that a mistake is shown next to its field instead of coming back as a failed apply. Only the
 * subset of JSON Schema the params schemas use is understood: objects, strings (with `enum`,
 * `minLength`, `maxLength`, `pattern`), integers, booleans, arrays of strings, and maps by id
 * (`additionalProperties` with a schema, `propertyNames`, `minProperties`, `maxProperties`), as
 * the assistants of a workspace.
 */

/** The schema of one assistant: the params of every image version are assistants by id (`assistants`). */
export const assistantSchema = (schema) => schema?.properties?.assistants?.additionalProperties ?? null;

/** The ids of the assistants of params, sorted. */
export const assistantIds = (params) => Object.keys(params?.assistants ?? {}).sort();

/**
 * The fields of a schema, flattened: nested objects become a dotted path and a group title.
 *
 * @returns {Array<{ path: string[], key: string, title: string, description?: string,
 *   kind: 'text' | 'multiline' | 'select' | 'list' | 'assistants' | 'boolean' | 'integer' | 'image', options?: string[],
 *   required: boolean, group?: string, schema: object }>}
 */
export function fieldsOf(schema, path = [], group = undefined) {
  const fields = [];
  const required = schema.required ?? [];
  for (const [name, property] of Object.entries(schema.properties ?? {})) {
    const fieldPath = [...path, name];
    if (property.type === 'object') {
      fields.push(...fieldsOf(property, fieldPath, property.title ?? name));
      continue;
    }
    fields.push({
      path: fieldPath,
      key: fieldPath.join('.'),
      title: property.title ?? name,
      description: property.description,
      kind: kindOf(property),
      options: property.enum,
      required: required.includes(name),
      group,
      schema: property,
    });
  }
  return fields;
}

function kindOf(property) {
  if (property.enum) {
    return 'select';
  }
  // a picture as a data URI, chosen from a file (`"x-widget": "image"`)
  if (property['x-widget'] === 'image') {
    return 'image';
  }
  // some of the other assistants of the workspace, by id (`"x-widget": "assistants"`): the delegates of a team
  if (property['x-widget'] === 'assistants') {
    return 'assistants';
  }
  // a longer text, on several lines (`"x-widget": "multiline"`)
  if (property['x-widget'] === 'multiline') {
    return 'multiline';
  }
  switch (property.type) {
    case 'array':
      return 'list';
    case 'boolean':
      return 'boolean';
    case 'integer':
      return 'integer';
    default:
      return 'text';
  }
}

/** The value at a path, or undefined. */
export function getAt(object, path) {
  return path.reduce((value, key) => (value == null ? undefined : value[key]), object);
}

/**
 * A copy of `object` with `value` at `path`. An undefined value removes the key, and an object
 * left empty by the removal is removed too: an absent optional field is not sent as `""`.
 */
export function setAt(object, path, value) {
  const [head, ...rest] = path;
  const copy = { ...(object ?? {}) };
  const next = rest.length === 0 ? value : setAt(copy[head], rest, value);
  const empty = next === undefined || (typeof next === 'object' && next !== null && !Array.isArray(next) && Object.keys(next).length === 0);
  if (empty) {
    delete copy[head];
  } else {
    copy[head] = next;
  }
  return copy;
}

/** A list field is edited one entry per line. */
export function parseList(text) {
  const items = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  return items.length > 0 ? items : undefined;
}

/**
 * The errors of `params` against `schema`, by field key; empty when the params are valid.
 * Unknown fields are errors too, as they are for the clawlet: a revision with a field the schema
 * does not declare is refused.
 *
 * @returns {Record<string, string>}
 */
export function validate(schema, params, path = []) {
  const errors = {};
  const value = params ?? {};
  const properties = schema.properties ?? {};
  const each = typeof schema.additionalProperties === 'object' ? schema.additionalProperties : null;
  for (const name of schema.required ?? []) {
    if (value[name] === undefined || value[name] === '') {
      errors[[...path, name].join('.')] = 'Required.';
    }
  }
  const count = Object.keys(value).length;
  if (schema.minProperties !== undefined && count < schema.minProperties) {
    errors[path.join('.')] = `At least ${schema.minProperties}.`;
  }
  if (schema.maxProperties !== undefined && count > schema.maxProperties) {
    errors[path.join('.')] = `At most ${schema.maxProperties}.`;
  }
  for (const [name, item] of Object.entries(value)) {
    const key = [...path, name].join('.');
    const property = properties[name];
    if (!property && each) {
      // an entry of a map by id, as an assistant: its key, then its value
      const pattern = schema.propertyNames?.pattern;
      if (pattern && !new RegExp(pattern, 'u').test(name)) {
        errors[key] = 'Lowercase letters, digits and -, neither first nor last.';
        continue;
      }
      Object.assign(errors, validate(each, item, [...path, name]));
      continue;
    }
    if (!property) {
      errors[key] = 'Not a field of this image version.';
      continue;
    }
    if (property.type === 'object') {
      Object.assign(errors, validate(property, item, [...path, name]));
      continue;
    }
    const problem = checkValue(property, item);
    if (problem) {
      errors[key] = problem;
    }
  }
  return errors;
}

function checkValue(property, value) {
  if (property.enum && !property.enum.includes(value)) {
    return `One of: ${property.enum.join(', ')}.`;
  }
  switch (property.type) {
    case 'string':
      return checkString(property, value);
    case 'integer':
      return Number.isInteger(value) ? null : 'A whole number.';
    case 'boolean':
      return typeof value === 'boolean' ? null : 'Yes or no.';
    case 'array': {
      if (!Array.isArray(value)) {
        return 'A list.';
      }
      const bad = value.find((item) => checkValue(property.items ?? { type: 'string' }, item));
      return bad === undefined ? null : `"${bad}": ${checkValue(property.items ?? { type: 'string' }, bad)}`;
    }
    default:
      return null;
  }
}

function checkString(property, value) {
  if (typeof value !== 'string') {
    return 'Text.';
  }
  if (property.minLength !== undefined && value.length < property.minLength) {
    return property.minLength === 1 ? 'Required.' : `At least ${property.minLength} characters.`;
  }
  if (property.maxLength !== undefined && value.length > property.maxLength) {
    return `At most ${property.maxLength} characters.`;
  }
  if (property.pattern && !new RegExp(property.pattern, 'u').test(value)) {
    return property.description ?? `Does not match ${property.pattern}.`;
  }
  return null;
}

/**
 * The values of params only an operator may change, by dotted path (`model`, or
 * `assistants.<id>.model` for an assistant): a customer user keeps them as the operator chose
 * them, so an assistant added or taken away changes them too (backend: ParamsSchemas.operatorValues).
 *
 * @returns {Record<string, unknown>} a missing value is undefined
 */
export function operatorValues(schema, params, prefix = '') {
  const values = {};
  const value = params ?? {};
  const properties = schema.properties ?? {};
  for (const [name, property] of Object.entries(properties)) {
    if (property['x-audience'] === 'operator') {
      values[prefix + name] = value[name];
    } else if (property.type === 'object') {
      Object.assign(values, operatorValues(property, value[name], `${prefix}${name}.`));
    }
  }
  if (typeof schema.additionalProperties === 'object') {
    for (const [name, item] of Object.entries(value)) {
      if (!(name in properties)) {
        Object.assign(values, operatorValues(schema.additionalProperties, item, `${prefix}${name}.`));
      }
    }
  }
  return values;
}

/**
 * The top-level parameters only an operator may change (`"x-audience": "operator"`): a customer
 * user keeps the values of the desired revision. The backend refuses a revision that changes them.
 */
export function operatorParams(schema) {
  return Object.entries(schema.properties ?? {})
    .filter(([, property]) => property['x-audience'] === 'operator')
    .map(([name]) => name);
}
